import { useViewport } from "@faire/react/viewport/useViewport";
import { Add } from "@faire/slate-icons/Add";
import { ArrowLeft } from "@faire/slate-icons/ArrowLeft";
import { Check } from "@faire/slate-icons/Check";
import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { TextInput } from "@faire/slate/components/text-input";
import { tsv } from "@faire/slate/lib/tsv";
import {
  LayoutGroup,
  motion,
  type Variants,
  useReducedMotion,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { ALL_FAVORITES_COLLECTION_ID } from "./savedStore";
import { UnfavoriteConfirmationModal } from "./UnfavoriteConfirmationModal";

const POPOVER_WIDTH = 320;
// Sized so the scrollable collection list is clipped halfway through the 4th
// row (68px header + 176px list + 1px divider + 72px footer), making it clear
// the list scrolls rather than ending on a fully-visible row.
const POPOVER_MAX_HEIGHT = 317;
const VIEWPORT_MARGIN = 12;
const ANCHOR_OFFSET = 8;
const COLLECTION_LIST_HORIZONTAL_PADDING = 16;
const SELECTION_INDICATOR_SIZE = 32;
const SELECTION_INDICATOR_CENTER_FROM_POPOVER_LEFT =
  POPOVER_WIDTH -
  COLLECTION_LIST_HORIZONTAL_PADDING -
  SELECTION_INDICATOR_SIZE / 2;
// Delay before the popover closes after adding/removing a collection. Each
// toggle restarts it, so a retailer can add/remove across several collections
// before the popover dismisses.
const UNFAVORITE_FEEDBACK_DELAY_MS = 2000;
// Removing the product from its last collection (unsaving entirely) leaves
// nothing left to organize, so close faster.
const LAST_FAVORITE_REMOVAL_CLOSE_DELAY_MS = 1000;
// "Grow from the tap point" reveal: a compositor-friendly scale + fade anchored
// at the popover's transform origin, with an easeOutExpo-style curve so it feels
// like it springs open. Scale/opacity stay on the GPU, so main-thread work
// (React commit, image decode) can't stall the animation the way clip-path did.
const POPOVER_CLOSED_SCALE = 0.8;
const POPOVER_REVEAL_TRANSITION = {
  duration: 0.34,
  ease: [0.16, 1, 0.3, 1],
} as const;
const POPOVER_FLOW_LAYOUT_TRANSITION = {
  duration: 0.25,
  ease: [0.45, 0, 0.55, 1],
} as const;
const COLLECTION_COVER_IMAGE_TRANSITION = {
  duration: 0.14,
  ease: "easeOut",
} as const;
const COLLECTION_THUMBNAIL_VARIANTS: Variants = {
  idle: {
    scale: 1,
  },
  add: {
    scale: [1, 1.1, 0.94, 1],
    transition: {
      duration: 0.3,
      ease: "easeOut",
      times: [0, 0.32, 0.68, 1],
    },
  },
  remove: {
    scale: [1, 0.9, 1.06, 1],
    transition: {
      duration: 0.3,
      ease: "easeOut",
      times: [0, 0.32, 0.68, 1],
    },
  },
};
const REDUCED_MOTION_COLLECTION_THUMBNAIL_VARIANTS: Variants = {
  idle: { scale: 1 },
  add: { scale: 1, transition: { duration: 0 } },
  remove: { scale: 1, transition: { duration: 0 } },
};

export interface CollectionImageTransition {
  backgroundImageUrl: string | undefined;
  coverImageUrl: string;
  direction: "add" | "remove";
  id: number;
}

export interface FavoriteCollection {
  id: string;
  name: string;
  imageUrl?: string;
  imageTransition?: CollectionImageTransition;
}

export type PopoverAnchorLocation = "product-card" | "heart";

interface FavoriteCollectionPickerProps {
  anchorElement: HTMLElement;
  anchorLocation: PopoverAnchorLocation;
  closeOnCollectionSelect: boolean;
  collections: FavoriteCollection[];
  /** Condensed mode: open collapsed to a "Saved" header + "Add to collection"
   * row that expands into the full collection list on demand. */
  condensed?: boolean;
  /** Open the collections list already expanded, skipping the collapsed
   * "Add to collection" step. Defaults to `!condensed`. */
  startExpanded?: boolean;
  /** R2 condensed treatment: collapsed header has no "Remove" (only shown once
   * expanded) and the collapsed "Add to collection" row shows a placeholder. */
  r2?: boolean;
  /** R2 re-open on an already-saved product: open to a summary of the
   * collections it's saved to (rather than the full list). */
  savedSummary?: boolean;
  /** Condensed R3: adding/removing collections is staged and only committed
   * when the retailer presses "Save" (the popover no longer auto-closes after
   * a toggle). A fresh save with no organizing still auto-dismisses. */
  confirmWithSave?: boolean;
  /** Popover R2 treatment: a persistent "Saved" header with a filled check and
   * an always-expanded "Add to collection" list (no collapse/expand step). */
  popoverR2?: boolean;
  productImageUrl: string;
  selectedCollectionIds: string[];
  onClose: () => void;
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  onCreateCollection: (collectionName: string) => void;
  onExitComplete: () => void;
  onToggleCollection: (collectionId: string) => void;
  open: boolean;
}

export const FavoriteCollectionPicker: React.FC<
  FavoriteCollectionPickerProps
> = ({
  anchorElement,
  anchorLocation,
  closeOnCollectionSelect,
  collections,
  condensed: condensedProp,
  startExpanded: startExpandedProp,
  r2: r2Prop,
  savedSummary: savedSummaryProp,
  confirmWithSave: confirmWithSaveProp,
  popoverR2: popoverR2Prop,
  productImageUrl,
  selectedCollectionIds,
  onClose,
  onCollectionImageTransitionComplete,
  onCreateCollection,
  onExitComplete,
  onToggleCollection,
  open,
}) => {
  const { isMobile } = useViewport();
  const condensed = condensedProp ?? false;
  const [isUnfavoriteConfirmationOpen, setIsUnfavoriteConfirmationOpen] =
    useState(false);
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [collectionName, setCollectionName] = useState("");
  const unfavoriteFeedbackTimeoutRef = useRef<number | null>(null);

  const clearUnfavoriteFeedbackTimeout = () => {
    if (unfavoriteFeedbackTimeoutRef.current === null) {
      return;
    }
    window.clearTimeout(unfavoriteFeedbackTimeoutRef.current);
    unfavoriteFeedbackTimeoutRef.current = null;
  };

  const closeAfterUnfavoriteFeedback = (
    delayMs = UNFAVORITE_FEEDBACK_DELAY_MS
  ) => {
    clearUnfavoriteFeedbackTimeout();
    unfavoriteFeedbackTimeoutRef.current = window.setTimeout(() => {
      unfavoriteFeedbackTimeoutRef.current = null;
      onClose();
    }, delayMs);
  };

  const handleToggleCollectionClick = (collectionId: string) => {
    clearUnfavoriteFeedbackTimeout();
    const removesProductFromOtherCollections =
      collectionId === "all-favorites" &&
      selectedCollectionIds.includes(collectionId) &&
      selectedCollectionIds.length > 1;
    if (removesProductFromOtherCollections) {
      setIsUnfavoriteConfirmationOpen(true);
      return;
    }
    onToggleCollection(collectionId);
    const removesLastFavorite =
      collectionId === "all-favorites" &&
      selectedCollectionIds.includes(collectionId) &&
      selectedCollectionIds.length === 1;
    if (closeOnCollectionSelect || removesLastFavorite) {
      closeAfterUnfavoriteFeedback(
        removesLastFavorite
          ? LAST_FAVORITE_REMOVAL_CLOSE_DELAY_MS
          : UNFAVORITE_FEEDBACK_DELAY_MS
      );
    }
  };

  useEffect(
    () => () => {
      if (unfavoriteFeedbackTimeoutRef.current !== null) {
        window.clearTimeout(unfavoriteFeedbackTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (isUnfavoriteConfirmationOpen) {
          setIsUnfavoriteConfirmationOpen(false);
        } else if (isCreatingCollection) {
          setIsCreatingCollection(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isCreatingCollection, isUnfavoriteConfirmationOpen, onClose]);

  useEffect(() => {
    if (!open && (isMobile || isUnfavoriteConfirmationOpen)) {
      onExitComplete();
    }
  }, [isMobile, isUnfavoriteConfirmationOpen, onExitComplete, open]);

  if (!open && isMobile) {
    return null;
  }

  const renderPickerContent = () => {
    if (isCreatingCollection) {
      return (
        <NewCollectionForm
          collectionName={collectionName}
          key="new-collection-form"
          onBack={() => setIsCreatingCollection(false)}
          onCollectionNameChange={setCollectionName}
          onSubmit={() => {
            const trimmedCollectionName = collectionName.trim();
            if (!trimmedCollectionName) {
              return;
            }
            onCreateCollection(trimmedCollectionName);
          }}
          productImageUrl={productImageUrl}
        />
      );
    }
    if (popoverR2Prop) {
      return (
        <PopoverR2Content
          collections={collections}
          key="popover-r2"
          onCollectionImageTransitionComplete={
            onCollectionImageTransitionComplete
          }
          onRemove={() =>
            handleToggleCollectionClick(ALL_FAVORITES_COLLECTION_ID)
          }
          onStartCreateCollection={() => {
          clearUnfavoriteFeedbackTimeout();
          setIsCreatingCollection(true);
        }}
          onToggleCollection={handleToggleCollectionClick}
          productImageUrl={productImageUrl}
          selectedCollectionIds={selectedCollectionIds}
        />
      );
    }
    return (
      <CondensedPickerContent
        collections={collections}
        confirmWithSave={confirmWithSaveProp ?? false}
        key="condensed-picker"
        onCollectionImageTransitionComplete={onCollectionImageTransitionComplete}
        onCommitToggles={(collectionIds) => {
          // Commit the staged adds/removes in one go, then close — no
          // per-toggle close timer for the confirm-with-save flow.
          clearUnfavoriteFeedbackTimeout();
          collectionIds.forEach(onToggleCollection);
          onClose();
        }}
        onRequestClose={onClose}
        onRemove={() =>
          handleToggleCollectionClick(ALL_FAVORITES_COLLECTION_ID)
        }
        onStartCreateCollection={() => {
          clearUnfavoriteFeedbackTimeout();
          setIsCreatingCollection(true);
        }}
        onToggleCollection={handleToggleCollectionClick}
        productImageUrl={productImageUrl}
        r2={r2Prop ?? false}
        savedSummary={savedSummaryProp ?? false}
        selectedCollectionIds={selectedCollectionIds}
        startExpanded={startExpandedProp ?? !condensed}
      />
    );
  };
  const pickerContent = renderPickerContent();
  const confirmationModal = (
    <UnfavoriteConfirmationModal
      imageUrl={productImageUrl}
      onCancel={() => setIsUnfavoriteConfirmationOpen(false)}
      onConfirm={() => {
        onToggleCollection("all-favorites");
        onClose();
      }}
      open={isUnfavoriteConfirmationOpen}
    />
  );

  if (isMobile) {
    return (
      <>
        {isUnfavoriteConfirmationOpen
          ? null
          : createPortal(
              <div className={mobileOverlayStyle.cls()} onMouseDown={onClose}>
                <div
                  aria-label="Save to collections"
                  aria-modal="true"
                  className={mobileDrawerStyle.cls()}
                  data-test-id="favorites-drawer"
                  role="dialog"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  {isCreatingCollection ? null : (
                    <div className={drawerHeaderStyle.cls()}>
                      <div aria-hidden className={drawerHandleStyle.cls()} />
                      <p className="type-fs-subheading-medium">
                        Save to collections
                      </p>
                      <IconButton
                        aria-label="Close"
                        Icon={Close}
                        onClick={onClose}
                        size="xSmall"
                        variant="tertiary"
                      />
                    </div>
                  )}
                  {pickerContent}
                </div>
              </div>,
              document.body
            )}
        {confirmationModal}
      </>
    );
  }

  return (
    <>
      {isUnfavoriteConfirmationOpen ? null : (
        <DesktopFavoriteCollectionPopover
          anchorElement={anchorElement}
          anchorLocation={anchorLocation}
          maxHeight={POPOVER_MAX_HEIGHT}
          onClose={onClose}
          onExitComplete={onExitComplete}
          open={open}
        >
          {pickerContent}
        </DesktopFavoriteCollectionPopover>
      )}
      {confirmationModal}
    </>
  );
};

interface DesktopFavoriteCollectionPopoverProps {
  anchorElement: HTMLElement;
  anchorLocation: PopoverAnchorLocation;
  children: React.ReactNode;
  maxHeight: number;
  onClose: () => void;
  onExitComplete: () => void;
  open: boolean;
}

const DesktopFavoriteCollectionPopover: React.FC<
  DesktopFavoriteCollectionPopoverProps
> = ({
  anchorElement,
  anchorLocation,
  children,
  maxHeight,
  onClose,
  onExitComplete,
  open,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();
  const [position, setPosition] = useState(() =>
    getPopoverPosition(anchorElement, anchorLocation, maxHeight)
  );
  // The inner surface's `layout` animation and the parent's clip-path reveal
  // fight each other in Chrome (the layout projection recalculates mid-reveal,
  // producing a jump). Enable `layout` only once the reveal has finished, so
  // the collapsed→expanded interaction still animates but the open reveal is
  // driven purely by the clip-path.
  const [hasRevealed, setHasRevealed] = useState(false);

  useEffect(() => {
    if (!open) {
      setHasRevealed(false);
    }
  }, [open]);

  const updatePosition = useCallback(() => {
    const nextPosition = getPopoverPosition(
      anchorElement,
      anchorLocation,
      maxHeight
    );
    setPosition((currentPosition) =>
      currentPosition.bottom === nextPosition.bottom &&
      currentPosition.left === nextPosition.left &&
      currentPosition.top === nextPosition.top &&
      currentPosition.transformOrigin === nextPosition.transformOrigin
        ? currentPosition
        : nextPosition
    );
  }, [anchorElement, anchorLocation, maxHeight]);

  useLayoutEffect(() => {
    updatePosition();
    const handleScroll = (event: Event) => {
      if (!popoverRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [onClose, updatePosition]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose]);

  return createPortal(
    <LayoutGroup>
      <motion.div
        animate={
          open
            ? { opacity: 1, scale: 1 }
            : { opacity: 0, scale: POPOVER_CLOSED_SCALE }
        }
        ref={popoverRef}
        aria-label="Save to collections"
        className={desktopPopoverPositionerStyle.cls()}
        data-test-id="favorites-popover"
        initial={
          shouldReduceMotion
            ? false
            : { opacity: 0, scale: POPOVER_CLOSED_SCALE }
        }
        onAnimationComplete={() => {
          if (open) {
            setHasRevealed(true);
          } else {
            onExitComplete();
          }
        }}
        role="dialog"
        style={{
          bottom: position.bottom,
          left: position.left,
          pointerEvents: open ? "auto" : "none",
          top: position.top,
          // Scale + opacity are compositor-accelerated in Chrome, so the open
          // animation can't be stalled mid-flight by main-thread work (React
          // committing the list, image decode) the way clip-path was.
          transformOrigin: position.transformOrigin,
          willChange: "opacity, transform",
          width: POPOVER_WIDTH,
        }}
        transition={
          shouldReduceMotion ? { duration: 0 } : POPOVER_REVEAL_TRANSITION
        }
      >
        <motion.div
          className={desktopPopoverSurfaceStyle.cls()}
          layout={!shouldReduceMotion && hasRevealed}
          style={{
            boxShadow: "0 2px 12px 0 rgba(0, 0, 0, 0.15)",
            maxHeight,
          }}
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : { layout: POPOVER_FLOW_LAYOUT_TRANSITION }
          }
        >
          {children}
        </motion.div>
      </motion.div>
    </LayoutGroup>,
    document.body
  );
};

const getPopoverPosition = (
  anchorElement: HTMLElement,
  anchorLocation: PopoverAnchorLocation,
  maxHeight: number
) => {
  const anchorRect = anchorElement.getBoundingClientRect();
  const anchorBottom =
    anchorLocation === "heart"
      ? anchorRect.bottom
      : anchorRect.top + anchorRect.width;
  const preferredTop = anchorBottom + ANCHOR_OFFSET;
  const fitsBelow =
    preferredTop + maxHeight + VIEWPORT_MARGIN <= window.innerHeight;
  const top = fitsBelow ? preferredTop : undefined;
  const preferredBottom = window.innerHeight - anchorRect.top + ANCHOR_OFFSET;
  const maxBottom = window.innerHeight - VIEWPORT_MARGIN - maxHeight;
  const bottom = fitsBelow ? undefined : Math.min(preferredBottom, maxBottom);
  const rightAlignedLeft = anchorRect.right - POPOVER_WIDTH;
  const shouldAlignLeft = rightAlignedLeft < VIEWPORT_MARGIN;
  const anchorCenter = anchorRect.left + anchorRect.width / 2;
  const heartAlignedLeft =
    anchorCenter - SELECTION_INDICATOR_CENTER_FROM_POPOVER_LEFT;
  let preferredLeft = rightAlignedLeft;
  if (anchorLocation === "heart") {
    preferredLeft = heartAlignedLeft;
  } else if (shouldAlignLeft) {
    preferredLeft = anchorRect.left;
  }
  const maxLeft = window.innerWidth - POPOVER_WIDTH - VIEWPORT_MARGIN;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(preferredLeft, maxLeft));
  let horizontalTransformOrigin = "right";
  if (anchorLocation === "heart") {
    horizontalTransformOrigin = `${Math.max(
      0,
      Math.min(anchorCenter - left, POPOVER_WIDTH)
    )}px`;
  } else if (shouldAlignLeft) {
    horizontalTransformOrigin = "left";
  }

  const revealOriginX =
    horizontalTransformOrigin === "right"
      ? "100%"
      : horizontalTransformOrigin === "left"
        ? "0%"
        : horizontalTransformOrigin;
  const revealOriginY = fitsBelow ? "0%" : "100%";

  return {
    bottom,
    left,
    top,
    transformOrigin: `${horizontalTransformOrigin} ${
      fitsBelow ? "top" : "bottom"
    }`,
    revealOriginX,
    revealOriginY,
  };
};

interface CondensedPickerContentProps {
  collections: FavoriteCollection[];
  productImageUrl: string;
  /** R2: hide "Remove" while collapsed, and show a placeholder on the collapsed
   * "Add to collection" row. */
  r2: boolean;
  selectedCollectionIds: string[];
  /** Open already expanded (skip the collapsed "Add to collection" step). */
  startExpanded?: boolean;
  /** R2 re-open on an already-saved product: open to a summary of the
   * collections it's saved to, with an "Add to collection" button that expands
   * to the full list. */
  savedSummary?: boolean;
  /** Stage collection toggles locally and commit them via `onCommitToggles`
   * when the retailer presses "Save". */
  confirmWithSave: boolean;
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  /** Commit the staged collection toggles (one id per add/remove) and close. */
  onCommitToggles: (collectionIds: string[]) => void;
  /** Close the popover — used to auto-dismiss the collapsed post-save state
   * after a few seconds of no interaction. */
  onRequestClose: () => void;
  onRemove: () => void;
  onStartCreateCollection: () => void;
  onToggleCollection: (collectionId: string) => void;
}

type CondensedView = "collapsed" | "summary" | "full";

// Auto-dismiss the collapsed post-save popover after this idle delay.
const CONDENSED_AUTO_DISMISS_MS = 2000;

// Selected collections first (in their original order), then the rest — keeps
// the summary and the expanded list in the same order.
const orderCollectionsSelectedFirst = (
  collections: FavoriteCollection[],
  selectedCollectionIds: string[]
): FavoriteCollection[] => [
  ...collections.filter((collection) =>
    selectedCollectionIds.includes(collection.id)
  ),
  ...collections.filter(
    (collection) => !selectedCollectionIds.includes(collection.id)
  ),
];

const CondensedPickerContent: React.FC<CondensedPickerContentProps> = ({
  collections,
  productImageUrl,
  r2,
  selectedCollectionIds,
  startExpanded,
  savedSummary,
  confirmWithSave,
  onCollectionImageTransitionComplete,
  onCommitToggles,
  onRequestClose,
  onRemove,
  onStartCreateCollection,
  onToggleCollection,
}) => {
  const shouldReduceMotion = useReducedMotion();
  const organizableCollections = collections.filter(
    (collection) => collection.id !== ALL_FAVORITES_COLLECTION_ID
  );
  const getInitialView = (): CondensedView => {
    if (startExpanded) {
      return "full";
    }
    return savedSummary ? "summary" : "collapsed";
  };
  const [initialView] = useState<CondensedView>(getInitialView);
  // The summary list is frozen to the collections the product was in when the
  // popover opened, so removing one keeps its row (indicator flips check → plus)
  // rather than making it disappear.
  const [summaryCollectionIds] = useState<string[]>(() =>
    getInitialView() === "summary"
      ? organizableCollections
          .filter((collection) => selectedCollectionIds.includes(collection.id))
          .map((collection) => collection.id)
      : []
  );
  const summaryCollections = summaryCollectionIds
    .map((id) => organizableCollections.find((collection) => collection.id === id))
    .filter((collection): collection is FavoriteCollection =>
      Boolean(collection)
    );
  const [view, setView] = useState<CondensedView>(initialView);
  const [hasInteracted, setHasInteracted] = useState(false);
  // The expanded list order is frozen when the list opens (selected-first) so
  // toggling collections doesn't reshuffle rows underneath the user.
  const [fullOrderIds, setFullOrderIds] = useState<string[] | null>(() =>
    initialView === "full"
      ? orderCollectionsSelectedFirst(
          organizableCollections,
          selectedCollectionIds
        ).map((collection) => collection.id)
      : null
  );

  // Confirm-with-save (R3): collection toggles are staged here and only
  // committed when the retailer presses "Save", so the popover stays open until
  // they confirm instead of auto-dismissing after each toggle.
  const committedOrganizableIds = selectedCollectionIds.filter(
    (collectionId) => collectionId !== ALL_FAVORITES_COLLECTION_ID
  );
  const [pendingSelectedIds, setPendingSelectedIds] = useState<string[]>(
    committedOrganizableIds
  );
  const effectiveSelectedIds = confirmWithSave
    ? pendingSelectedIds
    : selectedCollectionIds;
  const hasPendingChanges =
    confirmWithSave &&
    (pendingSelectedIds.length !== committedOrganizableIds.length ||
      pendingSelectedIds.some(
        (collectionId) => !committedOrganizableIds.includes(collectionId)
      ));
  const handleRowToggle = (collectionId: string) => {
    if (!confirmWithSave) {
      onToggleCollection(collectionId);
      return;
    }
    setPendingSelectedIds((currentPendingSelectedIds) =>
      currentPendingSelectedIds.includes(collectionId)
        ? currentPendingSelectedIds.filter(
            (pendingId) => pendingId !== collectionId
          )
        : [...currentPendingSelectedIds, collectionId]
    );
  };
  const handleSave = () => {
    const changedCollectionIds = [
      ...pendingSelectedIds.filter(
        (collectionId) => !committedOrganizableIds.includes(collectionId)
      ),
      ...committedOrganizableIds.filter(
        (collectionId) => !pendingSelectedIds.includes(collectionId)
      ),
    ];
    onCommitToggles(changedCollectionIds);
  };
  const saveFooter = (
    <div className={newCollectionFooterStyle.cls()}>
      <Button fullWidth onClick={handleSave} type="button">
        Save
      </Button>
    </div>
  );

  // Fresh save (collapsed) auto-dismisses after a few idle seconds; any
  // interaction with the popover cancels it.
  useEffect(() => {
    if (initialView !== "collapsed" || hasInteracted) {
      return undefined;
    }
    const timeoutId = window.setTimeout(
      onRequestClose,
      CONDENSED_AUTO_DISMISS_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [initialView, hasInteracted, onRequestClose]);

  const expandToFull = () => {
    setFullOrderIds(
      orderCollectionsSelectedFirst(
        organizableCollections,
        selectedCollectionIds
      ).map((collection) => collection.id)
    );
    setView("full");
  };
  const orderedFullCollections = fullOrderIds
    ? [
        ...fullOrderIds
          .map((id) =>
            organizableCollections.find((collection) => collection.id === id)
          )
          .filter((collection): collection is FavoriteCollection =>
            Boolean(collection)
          ),
        // Collections created after the list opened aren't in the frozen order.
        ...organizableCollections.filter(
          (collection) => !fullOrderIds.includes(collection.id)
        ),
      ]
    : organizableCollections;
  const isFull = view === "full";
  // R2 collapsed header hides Remove (it returns once expanded).
  const showRemove = !r2 || isFull;
  // R2 swaps the "Remove" text link for a check/plus icon button on the "Saved"
  // row. Like a collection toggle, it flips between a primary check (saved) and
  // a secondary plus (removed) instead of clearing immediately.
  const savedInAllFavorites = selectedCollectionIds.includes(
    ALL_FAVORITES_COLLECTION_ID
  );
  const renderSavedRowAction = () => {
    if (r2) {
      return savedInAllFavorites ? (
        <IconButton
          aria-label="Remove from saved"
          Icon={Check}
          onClick={onRemove}
          size="xSmall"
          type="button"
          variant="primary"
        />
      ) : (
        <IconButton
          aria-label="Add to saved"
          Icon={Add}
          onClick={onRemove}
          size="xSmall"
          type="button"
          variant="secondary"
        />
      );
    }
    if (showRemove) {
      return (
        <button
          className={condensedRemoveButtonStyle.cls()}
          onClick={onRemove}
          type="button"
        >
          Remove
        </button>
      );
    }
    return null;
  };
  const savedRowAction = renderSavedRowAction();

  const markInteracted = () => setHasInteracted(true);

  return (
    <motion.div
      className={pickerContentStyle.cls()}
      layout={shouldReduceMotion ? false : "position"}
      onFocusCapture={markInteracted}
      onPointerDown={markInteracted}
      onPointerMove={markInteracted}
      transition={
        shouldReduceMotion ? { duration: 0 } : POPOVER_FLOW_LAYOUT_TRANSITION
      }
    >
      <div className={condensedSavedRowStyle.cls()}>
        <div className={collectionThumbnailStyle.cls()}>
          <img
            alt=""
            className={collectionImageStyle.cls()}
            src={productImageUrl}
          />
          <ProductImageScrim />
        </div>
        <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">Saved</p>
        {savedRowAction}
      </div>
      {view === "full" ? (
        <>
          <div className={scrollAreaStyle.cls()}>
            <div className={collectionListStyle.cls()}>
              {orderedFullCollections.map((collection) => (
                <CollectionRow
                  collection={collection}
                  isSelected={effectiveSelectedIds.includes(collection.id)}
                  key={collection.id}
                  onCollectionImageTransitionComplete={
                    onCollectionImageTransitionComplete
                  }
                  onToggleCollection={handleRowToggle}
                />
              ))}
            </div>
          </div>
          <div className={footerDividerStyle.cls({ spaced: hasPendingChanges })} />
          {hasPendingChanges ? (
            saveFooter
          ) : (
            <div className={newCollectionFooterStyle.cls()}>
              <button
                aria-label="Create a new collection"
                className={collectionRowStyle.cls()}
                onClick={onStartCreateCollection}
                type="button"
              >
                <NewCollectionPlaceholder />
                <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
                  New collection
                </p>
                <SelectionIndicator isSelected={false} />
              </button>
            </div>
          )}
        </>
      ) : null}
      {view === "summary" ? (
        <>
          {summaryCollections.length > 0 ? (
            <div className={scrollAreaStyle.cls()}>
              <div className={collectionListStyle.cls()}>
                {summaryCollections.map((collection) => (
                  <CollectionRow
                    collection={collection}
                    isSelected={effectiveSelectedIds.includes(collection.id)}
                    key={collection.id}
                    onCollectionImageTransitionComplete={
                      onCollectionImageTransitionComplete
                    }
                    onToggleCollection={handleRowToggle}
                  />
                ))}
              </div>
            </div>
          ) : null}
          {hasPendingChanges ? (
            <>
              <div className={footerDividerStyle.cls({ spaced: true })} />
              {saveFooter}
            </>
          ) : (
            <div
              className={
                summaryCollections.length > 0
                  ? summaryAddFooterStyle.cls()
                  : condensedAddFooterR2Style.cls()
              }
            >
              <button
                aria-expanded={false}
                className={collectionRowStyle.cls()}
                onClick={expandToFull}
                type="button"
              >
                <NewCollectionPlaceholder />
                <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
                  Add to collection
                </p>
                <SelectionIndicator isSelected={false} />
              </button>
            </div>
          )}
        </>
      ) : null}
      {view === "collapsed" ? (
        <>
          {r2 ? null : <div className="border-fs-border-muted border-t" />}
          <div
            className={
              r2
                ? condensedAddFooterR2Style.cls()
                : condensedAddFooterStyle.cls()
            }
          >
            <button
              aria-expanded={false}
              className={collectionRowStyle.cls()}
              onClick={expandToFull}
              type="button"
            >
              {r2 ? <NewCollectionPlaceholder /> : null}
              <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
                Add to collection
              </p>
              {r2 ? (
                <SelectionIndicator isSelected={false} />
              ) : (
                <span aria-hidden className={condensedChevronStyle.cls()}>
                  <ChevronDown height="20px" width="20px" />
                </span>
              )}
            </button>
          </div>
        </>
      ) : null}
    </motion.div>
  );
};

interface PopoverR2ContentProps {
  collections: FavoriteCollection[];
  productImageUrl: string;
  selectedCollectionIds: string[];
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  onRemove: () => void;
  onStartCreateCollection: () => void;
  onToggleCollection: (collectionId: string) => void;
}

const PopoverR2Content: React.FC<PopoverR2ContentProps> = ({
  collections,
  productImageUrl,
  selectedCollectionIds,
  onCollectionImageTransitionComplete,
  onRemove,
  onStartCreateCollection,
  onToggleCollection,
}) => {
  const organizableCollections = collections.filter(
    (collection) => collection.id !== ALL_FAVORITES_COLLECTION_ID
  );

  return (
    <div className={pickerContentStyle.cls()}>
      <div className={popoverR2HeaderStyle.cls()}>
        <div className={collectionThumbnailStyle.cls()}>
          <img
            alt=""
            className={collectionImageStyle.cls()}
            src={productImageUrl}
          />
          <ProductImageScrim />
        </div>
        <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">Saved</p>
        <button
          className={condensedRemoveButtonStyle.cls()}
          onClick={onRemove}
          type="button"
        >
          Remove
        </button>
      </div>
      <div className={popoverR2BodyStyle.cls()}>
        <p className="type-fs-label-medium text-fs-text-primary">
          Add to collection
        </p>
        <div className={popoverR2ListStyle.cls()}>
          {organizableCollections.map((collection) => (
            <CollectionRow
              collection={collection}
              isSelected={selectedCollectionIds.includes(collection.id)}
              key={collection.id}
              onCollectionImageTransitionComplete={
                onCollectionImageTransitionComplete
              }
              onToggleCollection={onToggleCollection}
            />
          ))}
          <button
            aria-label="Create a new collection"
            className={collectionRowStyle.cls()}
            onClick={onStartCreateCollection}
            type="button"
          >
            <NewCollectionPlaceholder />
            <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
              New collection
            </p>
            <SelectionIndicator isSelected={false} />
          </button>
        </div>
      </div>
    </div>
  );
};

const NewCollectionPlaceholder: React.FC = () => (
  <span aria-hidden className={newCollectionPlaceholderStyle.cls()}>
    <span className={newCollectionPlaceholderTileStyle.cls()} />
    <span className={newCollectionPlaceholderTileStyle.cls()} />
    <span className={newCollectionPlaceholderTileStyle.cls()} />
    <span className={newCollectionPlaceholderTileStyle.cls()} />
  </span>
);

interface NewCollectionFormProps {
  collectionName: string;
  productImageUrl: string;
  onBack: () => void;
  onCollectionNameChange: (collectionName: string) => void;
  onSubmit: () => void;
}

const NewCollectionForm: React.FC<NewCollectionFormProps> = ({
  collectionName,
  productImageUrl,
  onBack,
  onCollectionNameChange,
  onSubmit,
}) => {
  const [showCollectionNameError, setShowCollectionNameError] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.form
      className={newCollectionFormStyle.cls()}
      layout={shouldReduceMotion ? false : "position"}
      transition={
        shouldReduceMotion ? { duration: 0 } : POPOVER_FLOW_LAYOUT_TRANSITION
      }
      onSubmit={(event) => {
        event.preventDefault();
        if (!collectionName.trim()) {
          setShowCollectionNameError(true);
          return;
        }
        onSubmit();
      }}
    >
      <div className={newCollectionFormHeaderStyle.cls()}>
        <IconButton
          aria-label="Back to collections"
          Icon={ArrowLeft}
          onClick={onBack}
          size="xSmall"
          type="button"
          variant="tertiary"
        />
      </div>
      <div className={newCollectionFormBodyStyle.cls()}>
        <div
          className={newCollectionProductImageStyle.cls()}
          style={{
            boxShadow: "-1px 2px 4px 0 rgba(0, 0, 0, 0.12)",
          }}
        >
          <img
            alt=""
            className="size-full object-cover"
            src={productImageUrl}
          />
          <ProductImageScrim />
        </div>
        <div className="w-full">
          <TextInput
            autoFocus
            error={showCollectionNameError}
            label="Collection name"
            onChange={(event) => {
              onCollectionNameChange(event.target.value);
              if (event.target.value.trim()) {
                setShowCollectionNameError(false);
              }
            }}
            placeholder="Holiday cards 2027"
            value={collectionName}
          />
        </div>
      </div>
      <div className={newCollectionFormFooterStyle.cls()}>
        <Button fullWidth type="submit">
          Create
        </Button>
      </div>
    </motion.form>
  );
};

interface CollectionRowProps {
  collection: FavoriteCollection;
  isSelected: boolean;
  onCollectionImageTransitionComplete: (
    collectionId: string,
    transitionId: number
  ) => void;
  onToggleCollection: (collectionId: string) => void;
}

const CollectionRow: React.FC<CollectionRowProps> = ({
  collection,
  isSelected,
  onCollectionImageTransitionComplete,
  onToggleCollection,
}) => (
  <button
    aria-label={`${isSelected ? "Remove from" : "Add to"} ${collection.name}`}
    aria-pressed={isSelected}
    className={collectionRowStyle.cls()}
    onClick={() => onToggleCollection(collection.id)}
    type="button"
  >
    <CollectionThumbnail
      collection={collection}
      onTransitionComplete={onCollectionImageTransitionComplete}
    />
    <p className="type-fs-paragraph-medium min-w-0 flex-1 truncate">
      {collection.name}
    </p>
    <SelectionIndicator isSelected={isSelected} />
  </button>
);

const SelectionIndicator: React.FC<{ isSelected: boolean }> = ({
  isSelected,
}) => (
  <span aria-hidden className={selectionIndicatorStyle.cls({ isSelected })}>
    {isSelected ? (
      <Check
        color="var(--slate-color-icon-primary-inverse)"
        height="16px"
        width="16px"
      />
    ) : (
      <Add height="16px" width="16px" />
    )}
  </span>
);

const ProductImageScrim: React.FC = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-0"
    style={{
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.08)",
    }}
  />
);

const CollectionThumbnail: React.FC<{
  collection: FavoriteCollection;
  onTransitionComplete: (collectionId: string, transitionId: number) => void;
}> = ({ collection, onTransitionComplete }) => {
  const shouldReduceMotion = useReducedMotion();
  const imageTransition = collection.imageTransition;
  const isAddingCoverImage = imageTransition?.direction === "add";
  const coverImageInitialOpacity = isAddingCoverImage ? 0 : 1;
  const coverImageInitialScale = isAddingCoverImage ? 0.8 : 1;

  return (
    <motion.div
      animate={imageTransition?.direction ?? "idle"}
      className={collectionThumbnailStyle.cls()}
      initial="idle"
      key={imageTransition?.id ?? `${collection.id}-idle`}
      onAnimationComplete={() => {
        if (imageTransition) {
          onTransitionComplete(collection.id, imageTransition.id);
        }
      }}
      variants={
        shouldReduceMotion
          ? REDUCED_MOTION_COLLECTION_THUMBNAIL_VARIANTS
          : COLLECTION_THUMBNAIL_VARIANTS
      }
    >
      {imageTransition ? (
        <>
          {imageTransition.backgroundImageUrl ? (
            <img
              alt=""
              className={collectionImageStyle.cls()}
              src={imageTransition.backgroundImageUrl}
            />
          ) : null}
          <motion.img
            animate={
              isAddingCoverImage
                ? { opacity: 1, scale: 1 }
                : { opacity: 0, scale: 0.8 }
            }
            alt=""
            className={collectionImageStyle.cls()}
            initial={
              shouldReduceMotion
                ? false
                : {
                    opacity: coverImageInitialOpacity,
                    scale: coverImageInitialScale,
                  }
            }
            src={imageTransition.coverImageUrl}
            transition={
              shouldReduceMotion
                ? { duration: 0 }
                : COLLECTION_COVER_IMAGE_TRANSITION
            }
          />
        </>
      ) : collection.imageUrl ? (
        <img
          alt=""
          className={collectionImageStyle.cls()}
          src={collection.imageUrl}
        />
      ) : null}
      <ProductImageScrim />
    </motion.div>
  );
};


const desktopPopoverPositionerStyle = tsv({
  base: "fixed z-50",
});

const desktopPopoverSurfaceStyle = tsv({
  base: [
    "bg-fs-surface-primary rounded-fs-container-default",
    "flex w-full overflow-hidden",
  ],
});

const pickerContentStyle = tsv({
  base: "flex min-h-0 w-full flex-1 flex-col",
});

const newCollectionFormStyle = tsv({
  base: "flex h-full w-full flex-col",
});

const newCollectionFormHeaderStyle = tsv({
  base: [
    "flex h-[72px] shrink-0 items-center",
    "px-(--slate-spacing-element-large)",
  ],
});

const newCollectionFormBodyStyle = tsv({
  base: [
    "flex h-[200px] shrink-0 flex-col items-center",
    "gap-(--slate-spacing-element-medium) px-(--slate-spacing-element-large)",
  ],
});

const newCollectionFormFooterStyle = tsv({
  base: [
    "flex h-[84px] shrink-0 items-end",
    "px-(--slate-spacing-element-large) pt-(--slate-spacing-element-large) pb-(--slate-dimensions-5)",
  ],
});

const newCollectionProductImageStyle = tsv({
  base: [
    "bg-fs-surface-tertiary",
    "rounded-fs-component-default relative size-32 shrink-0 overflow-hidden",
  ],
});

const scrollAreaStyle = tsv({
  base: "relative min-h-0 flex-1 overflow-hidden",
});

const collectionListStyle = tsv({
  base: [
    "flex h-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    "gap-(--slate-spacing-group-medium) px-(--slate-spacing-element-large)",
  ],
});

const newCollectionFooterStyle = tsv({
  base: "shrink-0 p-(--slate-spacing-element-large)",
});

const condensedAddFooterStyle = tsv({
  base: [
    "shrink-0 px-(--slate-spacing-element-large)",
    "py-(--slate-dimensions-2)",
  ],
});

// R2 collapsed: no top padding — the 12px gap comes from the Saved row's bottom
// padding, matching the inter-collection gap (no divider).
const condensedAddFooterR2Style = tsv({
  base: [
    "shrink-0 px-(--slate-spacing-element-large)",
    "pb-(--slate-spacing-element-large)",
  ],
});

// Summary view: the "Add to collection" row sits below the saved-collections
// list, so it needs its own top gap matching the inter-collection spacing.
const summaryAddFooterStyle = tsv({
  base: [
    "shrink-0 px-(--slate-spacing-element-large)",
    "pt-(--slate-spacing-group-medium) pb-(--slate-spacing-element-large)",
  ],
});

// Divider above the footer. When the "Save" footer is showing, keep 12px between
// the last collection row and the border (matching the inter-collection gap).
const footerDividerStyle = tsv({
  base: "border-fs-border-muted border-t",
  variants: {
    spaced: {
      true: "mt-(--slate-spacing-element-medium)",
      false: "",
    },
  },
});

const collectionRowStyle = tsv({
  base: [
    "group focusable interactive rounded-fs-component-default flex min-h-10 w-full items-center text-left",
    "gap-(--slate-spacing-group-medium)",
  ],
});

// 12px bottom padding so the gap to the first collection row (or the collapsed
// "Add to collection" row) matches the 12px inter-collection gap.
const condensedSavedRowStyle = tsv({
  base: [
    "flex w-full items-center gap-(--slate-spacing-group-medium)",
    "px-(--slate-spacing-element-large) pt-(--slate-spacing-element-large) pb-(--slate-spacing-element-medium)",
  ],
});

const popoverR2HeaderStyle = tsv({
  base: [
    "border-fs-border-muted flex w-full shrink-0 items-center border-b",
    "gap-(--slate-spacing-group-medium) p-(--slate-spacing-element-medium)",
  ],
});

const popoverR2BodyStyle = tsv({
  base: [
    "flex min-h-0 w-full flex-1 flex-col",
    "gap-(--slate-spacing-element-medium) p-(--slate-spacing-element-medium)",
    "pt-(--slate-spacing-element-large)",
  ],
});

const popoverR2ListStyle = tsv({
  base: [
    "flex min-h-0 flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    "gap-(--slate-spacing-element-medium)",
  ],
});

const condensedRemoveButtonStyle = tsv({
  base: "type-fs-paragraph focusable interactive shrink-0",
});

const condensedChevronStyle = tsv({
  base: "text-fs-icon-primary flex size-6 shrink-0 items-center justify-center",
});

const selectionIndicatorStyle = tsv({
  base: [
    "flex size-8 shrink-0 items-center justify-center",
    "rounded-fs-component-round transition-colors",
  ],
  variants: {
    isSelected: {
      true: [
        "bg-fs-action-surface-default text-fs-icon-primary-inverse",
        "group-hover:bg-fs-action-surface-hover",
      ],
      false: [
        "border-fs-action-border-subdued bg-fs-action-surface-inverse border",
        "group-hover:border-fs-action-border-hover group-hover:bg-fs-action-surface-inverse-hover",
      ],
    },
  },
});

const collectionThumbnailStyle = tsv({
  base: [
    "bg-fs-surface-tertiary",
    "rounded-fs-component-default relative size-10 shrink-0 overflow-hidden",
  ],
});

const collectionImageStyle = tsv({
  base: "absolute inset-0 size-full object-cover",
});

const newCollectionPlaceholderStyle = tsv({
  base: [
    "rounded-fs-component-default grid size-10 shrink-0 grid-cols-2 gap-0.5",
    "bg-fs-surface-primary overflow-hidden",
  ],
});

const newCollectionPlaceholderTileStyle = tsv({
  base: "bg-fs-surface-tertiary",
});

const mobileOverlayStyle = tsv({
  base: [
    "bg-fs-surface-mask fixed inset-0 z-50",
    "flex items-end justify-center",
  ],
});

const mobileDrawerStyle = tsv({
  base: [
    "bg-fs-surface-primary rounded-t-fs-container-default",
    "shadow-elevation-medium flex max-h-[85vh] w-full flex-col overflow-hidden",
  ],
});

const drawerHeaderStyle = tsv({
  base: [
    "border-fs-border-muted relative flex items-center justify-between border-b",
    "gap-(--slate-spacing-group-default) px-(--slate-spacing-element-large) py-(--slate-spacing-element-medium)",
  ],
});

const drawerHandleStyle = tsv({
  base: [
    "bg-fs-surface-tertiary absolute top-(--slate-spacing-element-xsmall) left-1/2",
    "rounded-fs-component-round h-1 w-10 -translate-x-1/2",
  ],
});
